import apiClient, { getLiveAccessToken } from "@/api/client";
import type {
  GuestInvite,
  GuestMeetingPreview,
  GuestRtcTokens,
  JoinContract,
  Meeting,
  MeetingDetail,
  MeetingNote,
  MeetingWritePayload,
  Occurrence,
  OccurrenceDetail,
  PeerId,
  RoomSignalType,
} from "../types";

const BASE = "/meetings";

function unwrap<T>(promise: Promise<{ data: { data: T } }>): Promise<T> {
  return promise.then((r) => r.data.data);
}

// ── meetings (series) ─────────────────────────────────────────────────────────

export const listMeetings = (params?: Record<string, string>) =>
  unwrap<Meeting[]>(apiClient.get(`${BASE}/`, { params }));

export const getMeeting = (id: string) =>
  unwrap<MeetingDetail>(apiClient.get(`${BASE}/${id}/`));

export const createMeeting = (payload: MeetingWritePayload) =>
  unwrap<MeetingDetail>(apiClient.post(`${BASE}/`, payload));

export const updateMeeting = (id: string, payload: Partial<MeetingWritePayload>) =>
  unwrap<MeetingDetail>(apiClient.patch(`${BASE}/${id}/`, payload));

export const cancelMeeting = (id: string) => apiClient.delete(`${BASE}/${id}/`);

export const respondToMeeting = (id: string, response: "accepted" | "declined") =>
  unwrap<{ rsvp: string }>(apiClient.post(`${BASE}/${id}/respond/`, { response }));

export const inviteToMeeting = (id: string, participantIds: string[]) =>
  unwrap<MeetingDetail>(
    apiClient.post(`${BASE}/${id}/invite/`, { participant_ids: participantIds }),
  );

export const toggleGuestNotes = (id: string, enabled: boolean) =>
  unwrap<{ guests_can_see_notes: boolean }>(
    apiClient.post(`${BASE}/${id}/guest-notes/`, { enabled }),
  );

// ── occurrences ───────────────────────────────────────────────────────────────

export const listOccurrences = (params: {
  from?: string;
  to?: string;
  status?: string;
  ze_project?: string;
}) => unwrap<Occurrence[]>(apiClient.get(`${BASE}/occurrences/`, { params }));

export const getOccurrence = (id: string) =>
  unwrap<OccurrenceDetail>(apiClient.get(`${BASE}/occurrences/${id}/`));

export const startOccurrence = (id: string) =>
  unwrap<OccurrenceDetail>(apiClient.post(`${BASE}/occurrences/${id}/start/`));

export const endOccurrence = (id: string) =>
  unwrap<OccurrenceDetail>(apiClient.post(`${BASE}/occurrences/${id}/end/`));

export const cancelOccurrence = (id: string) =>
  unwrap<OccurrenceDetail>(apiClient.post(`${BASE}/occurrences/${id}/cancel/`));

export const joinOccurrence = (id: string) =>
  unwrap<JoinContract>(apiClient.post(`${BASE}/occurrences/${id}/join/`));

export const leaveOccurrence = (id: string, session?: string) =>
  apiClient.post(`${BASE}/occurrences/${id}/leave/`, { session });

/** Best-effort leave that survives tab close (pagehide). */
export function leaveOccurrenceKeepalive(id: string, session?: string): void {
  const base = apiClient.defaults.baseURL ?? "/api/v1";
  // No time to refresh on pagehide, so an access token known to be expired is
  // not sent at all — the server could only answer it with a 401.
  const access = getLiveAccessToken();
  if (!access) return;
  // The join `session` scopes this to THIS tab: if a fast reload already
  // rejoined, the backend sees a session that no longer owns the row and
  // ignores the request instead of kicking the live tab out of the mesh.
  void fetch(`${base}${BASE}/occurrences/${id}/leave/`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
    },
    body: JSON.stringify({ session }),
  }).catch(() => {});
}

export const sendRoomSignal = (
  occurrenceId: string,
  msg: { to: PeerId | null; type: RoomSignalType; payload?: unknown },
) =>
  apiClient.post(`${BASE}/occurrences/${occurrenceId}/signal/`, {
    to: msg.to,
    type: msg.type,
    payload: msg.payload ?? {},
  });

// ── notes ─────────────────────────────────────────────────────────────────────

export const listNotes = (occurrenceId: string, phase?: string) =>
  unwrap<MeetingNote[]>(
    apiClient.get(`${BASE}/occurrences/${occurrenceId}/notes/`, {
      params: phase ? { phase } : undefined,
    }),
  );

export const addNote = (
  occurrenceId: string,
  payload: { phase: string; content: string },
) =>
  unwrap<MeetingNote>(
    apiClient.post(`${BASE}/occurrences/${occurrenceId}/notes/`, payload),
  );

export const updateNote = (noteId: string, payload: { content: string; phase?: string }) =>
  unwrap<MeetingNote>(apiClient.patch(`${BASE}/notes/${noteId}/`, payload));

export const deleteNote = (noteId: string) =>
  apiClient.delete(`${BASE}/notes/${noteId}/`);

// ── guest invites (organizer side) ────────────────────────────────────────────

export const listGuestInvites = (occurrenceId: string) =>
  unwrap<GuestInvite[]>(
    apiClient.get(`${BASE}/occurrences/${occurrenceId}/guest-invites/`),
  );

export const createGuestInvite = (
  occurrenceId: string,
  payload: { display_name?: string; email?: string },
) =>
  unwrap<GuestInvite>(
    apiClient.post(`${BASE}/occurrences/${occurrenceId}/guest-invites/`, payload),
  );

export const revokeGuestInvite = (inviteId: string) =>
  unwrap<GuestInvite>(apiClient.post(`${BASE}/guest-invites/${inviteId}/revoke/`));

// ── guest surface (token auth, no JWT) ────────────────────────────────────────
//
// Guest calls go through a bare axios-less fetch so no Authorization header or
// 401-refresh interceptor is ever involved.

const GUEST_BASE = () => `${apiClient.defaults.baseURL ?? "/api/v1"}${BASE}/guest`;

async function guestFetch<T>(
  path: string,
  init?: RequestInit & { keepalive?: boolean },
): Promise<T> {
  const res = await fetch(`${GUEST_BASE()}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(
      typeof body?.errors === "string" ? body.errors : `HTTP ${res.status}`,
    ) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return body?.data as T;
}

export const guestGetMeeting = (token: string) =>
  guestFetch<GuestMeetingPreview>(`/${encodeURIComponent(token)}/`);

export const guestJoin = (token: string, displayName: string) =>
  guestFetch<JoinContract>(`/${encodeURIComponent(token)}/join/`, {
    method: "POST",
    body: JSON.stringify({ display_name: displayName }),
  });

export const guestLeave = (token: string, session?: string, keepalive = false) =>
  guestFetch<{ ok: boolean }>(`/${encodeURIComponent(token)}/leave/`, {
    method: "POST",
    // The join session scopes the leave to this tab — see leaveOccurrenceKeepalive.
    body: JSON.stringify({ session }),
    keepalive,
  }).catch(() => undefined);

export const guestSignal = (
  token: string,
  msg: { to: PeerId | null; type: RoomSignalType; payload?: unknown },
) =>
  guestFetch<{ ok: boolean }>(`/${encodeURIComponent(token)}/signal/`, {
    method: "POST",
    body: JSON.stringify({ to: msg.to, type: msg.type, payload: msg.payload ?? {} }),
  });

export const guestRtcTokens = (token: string) =>
  guestFetch<GuestRtcTokens>(`/${encodeURIComponent(token)}/rtc-tokens/`);

export const guestGetNotes = (token: string) =>
  guestFetch<MeetingNote[]>(`/${encodeURIComponent(token)}/notes/`);

export function guestDiagnosticsSender(token: string) {
  return (events: unknown[]) =>
    guestFetch<{ accepted: number }>(`/${encodeURIComponent(token)}/diagnostics/`, {
      method: "POST",
      body: JSON.stringify({ events }),
    }).then(() => undefined);
}

export function guestDiagnosticsKeepalive(token: string) {
  return (events: unknown[]) => {
    void fetch(`${GUEST_BASE()}/${encodeURIComponent(token)}/diagnostics/`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events }),
    }).catch(() => {});
  };
}
